import type { ResponseReleaseStrategyExecuteDto } from "common/models/api/models/ResponseReleaseStrategyExecuteDto";
import useExecuteReleaseStrategy from "features/release-strategy/hooks/useExecuteReleaseStrategy";
import { useModal, useToast } from "@commonlibs";
import OtpModal from "ui/common/containers/Otp/containers/OtpModal/OtpModal";
import useTranslate from "ui/locale/hooks/useTranslate";
import { useRef } from "react";
import useUserState from "ui/adapter/state/useUserState";
import { RoleType } from "common/models/role/Role";
import useGetUserInfo from "features/users/hooks/useGetUserInfo";

interface VerifyReleaseMethodArgs {
    operationTypeId: number;
    originalFn: (...args: unknown[]) => void;
    originalArgs: unknown[];
    file?: File;
}

interface UseVerifyReleaseStrategyMethodProps {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
    onOtpRequired?: () => void;
    isLoading?: boolean;
}

export default function useVerifyReleaseStrategyMethod({
    onSuccess,
    onError,
    onOtpRequired,
    isLoading,
}: UseVerifyReleaseStrategyMethodProps = {}) {
    const { mutate, isLoading: isValidatingReleaseStrategy } = useExecuteReleaseStrategy({
        onSuccess: handleOnSuccess,
    });
    const { showModal, hideModal } = useModal();
    const { showToast } = useToast();
    const translateError = useTranslate("error");
    const { selectedRole } = useUserState();
    const { data: userData } = useGetUserInfo();

    const storedOriginalFnRef = useRef<((...args: unknown[]) => void) | null>(null);
    const storedOriginalArgsRef = useRef<unknown[]>([]);
    const storedOperationTypeIdRef = useRef<number | null>(null);
    const storedFileRef = useRef<File | null>(null);
    const validatedOtpRef = useRef<string | null>(null);

    function verifyReleaseMethod({ operationTypeId, originalFn, originalArgs, file }: VerifyReleaseMethodArgs) {
        storedOriginalFnRef.current = originalFn;
        storedOriginalArgsRef.current = originalArgs;
        storedOperationTypeIdRef.current = operationTypeId;
        storedFileRef.current = file ?? null;

        if (selectedRole?.roleType === RoleType.PARTNER) {
            triggerOriginalFunction();
            return;
        }

        const payload = {
            operationTypeId,
            userId: userData?.id as number,
            roleType: selectedRole?.roleType as RoleType,
            ...(selectedRole?.roleType === RoleType.PROGRAM_MANAGER && { programAddress: selectedRole?.program?.address }),
            ...(file && { file }),
        };

        mutate(payload);
    }

    function handleOnSuccess(data: ResponseReleaseStrategyExecuteDto) {
        if (data.release === "otp") {
            onOtpRequired?.();
            if (storedOriginalFnRef.current) {
                showOtpModal(storedOriginalFnRef.current, storedOriginalArgsRef.current);
            }
            return;
        }

        if (storedOriginalFnRef.current) {
            triggerOriginalFunction();
        } else {
            onSuccess?.();
        }
    }

    async function triggerOriginalFunction() { // skipcq: JS-0116
        try {
            storedOriginalFnRef.current?.(...storedOriginalArgsRef.current);
        } catch (error) {
            onError?.(error);
        }
    }

    function handleRegenerateOtp() {
        if (storedOperationTypeIdRef.current !== null && storedOriginalFnRef.current) {
            verifyReleaseMethod({
                operationTypeId: storedOperationTypeIdRef.current,
                originalFn: storedOriginalFnRef.current,
                originalArgs: storedOriginalArgsRef.current,
                file: storedFileRef.current ?? undefined,
            });
        }
    }

    function showOtpModal(originalFn: (...args: unknown[]) => void, originalArgs: unknown[]) {
        setTimeout(() => {
            validatedOtpRef.current = null;
            showModal(OtpModal, {
                // El OTP validado se envía como último argumento para que la acción lo incluya en su request
                action: (...args: unknown[]) => originalFn(...args, validatedOtpRef.current ?? undefined),
                actionArgs: originalArgs,
                onSubmit: (otp: string) => {
                    validatedOtpRef.current = otp;
                },
                onSuccess: () => {
                    hideModal(OtpModal.id);
                },
                onError: (error) => {
                    onError?.(error);
                    showToast(translateError("somethingWentWrong"), { type: "error" });
                },
                onRegenerateOtp: handleRegenerateOtp,
                isLoading: isLoading || isValidatingReleaseStrategy,
            });
        }, 1000);
    }

    return {
        verifyReleaseMethod,
        isValidatingReleaseStrategy,
    };
}
